/** 与 ScrollableChart / ComposedChart 共用的边距，须与图表 margin 一致 */
export const DEFAULT_CHART_MARGIN = { top: 20, right: 20, left: 20, bottom: 20 }

/** 百分比等场景：步长优先 5 / 10 及其数量级 */
const PERCENT_STEP_CANDIDATES = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]

/** 倍率场景 */
const MULTIPLIER_STEP_CANDIDATES = [0.1, 0.2, 0.5, 1, 2, 5, 10]

/**
 * 在候选步长中选取：刻度条数不超过约 tickCount 的前提下，取满足条件的最小步长
 * （区间窄时不会出现过大的步长）
 */
export function pickNiceStep(range, tickCount = 8, candidates = PERCENT_STEP_CANDIDATES) {
  if (range <= 0) return candidates[0] ?? 1
  const maxIntervals = tickCount + 1

  for (const step of candidates) {
    if (range / step <= maxIntervals) return step
  }
  return candidates[candidates.length - 1]
}

/** 按步长对齐，避免浮点误差 */
export function snapToStep(value, step) {
  const rounded = Math.round(value / step) * step
  if (step >= 1) return Math.round(rounded)
  const decimals = step >= 0.1 ? 1 : 2
  return Math.round(rounded * 10 ** decimals) / 10 ** decimals
}

/**
 * 根据 yMin/yMax（已含数据留白）生成「好看」刻度，步长为 5/10 或其倍数
 * @param {'percent'|'multiplier'|'auto'} mode
 */
export function buildNiceTicks(yMin, yMax, tickCount = 8, { mode = 'percent' } = {}) {
  const range = yMax - yMin
  if (range <= 0) return [yMin]

  let candidates = PERCENT_STEP_CANDIDATES
  if (mode === 'multiplier') {
    candidates = MULTIPLIER_STEP_CANDIDATES
  } else if (mode === 'auto') {
    const rough = range / Math.max(tickCount - 1, 1)
    const magnitude = 10 ** Math.floor(Math.log10(rough) || 0)
    candidates = [1, 2, 5, 10].map((m) => m * magnitude)
  }

  let step
  if (mode === 'percent') {
    const major = candidates.filter((s) => s >= 5)
    step = pickNiceStep(range, tickCount, major.length ? major : candidates)
  } else {
    step = pickNiceStep(range, tickCount, candidates)
  }
  const tickMin = snapToStep(Math.floor(yMin / step) * step, step)
  const tickMax = snapToStep(Math.ceil(yMax / step) * step, step)
  const count = Math.round((tickMax - tickMin) / step)

  const ticks = []
  for (let i = 0; i <= count; i++) {
    ticks.push(snapToStep(tickMin + i * step, step))
  }
  return ticks
}

/** @deprecated 仅保留兼容；请使用 buildNiceTicks */
export function buildLinearTicks(yMin, yMax, tickCount = 8) {
  return buildNiceTicks(yMin, yMax, tickCount, { mode: 'percent' })
}

/** 将数据值映射为相对图表容器顶部的像素位置（与 Recharts 绘图区对齐） */
export function valueToPlotTop(
  value,
  yMin,
  yMax,
  chartHeight,
  margin = DEFAULT_CHART_MARGIN,
) {
  const range = yMax - yMin
  if (range <= 0) return margin.top
  const percent = (value - yMin) / range
  const plotHeight = chartHeight - margin.top - margin.bottom
  return margin.top + plotHeight * (1 - percent)
}
